import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { useErrorHandler } from 'react-error-boundary'
import { useMachine } from '@xstate/react'

import { Episode as EpisodeLocation } from 'routing/routes'

import { Episode, EpisodeCodec } from 'model/Episode'
import { metadataMergeAutofill } from 'model/metadata/Metadata'
import { WIPDataType } from 'model/WIP'

import { useUser } from 'auth/Auth'

import { fetchResource } from 'api/fetch'
import { metadataFetchAutofill } from 'api/autofill/metadataFetchAutofill'

import { createResourceMachine } from 'shared/resource/resource.machine'
import Loader from 'shared/components/Loader'
import Header from 'shared/components/Header'
import HeaderEditing from 'shared/components/Header/Editing'
import CharactersProvider from 'shared/characters/CharactersProvider'
import { ResourceMachineServiceContext } from 'shared/resource/ResourceMachineProvider'
import { useRefreshLockedTitles } from 'shared/LockedTitles/LockedTitlesProvider'
import { useHasPortrayalsEditingRoles } from 'shared/hooks/useHasRole'
import { NotFoundError, ForbiddenError } from 'shared/errors/ServerError'

import EpisodeNode from 'pages/Episode/EpisodeNode'

const EpisodePage: React.FC = (): React.ReactElement => {
    const { episodeId } = useParams<EpisodeLocation>()
    const refreshLockedTitles = useRefreshLockedTitles()
    const handleError = useErrorHandler()
    const user = useUser()
    const canEditPortrayals = useHasPortrayalsEditingRoles()

    const wips: WIPDataType[] = useMemo(() => {
        return [
            'metadata',
            'storylines',
            'relationships',
            ...(canEditPortrayals ? ['characters' as WIPDataType] : []),
        ]
    }, [canEditPortrayals])

    const [state, , service] = useMachine(
        () =>
            createResourceMachine<Episode>({
                resourceId: episodeId,
                resourceType: 'episode',
                user,
                wips,
                fetchFn: ctx => fetchResource({ codec: EpisodeCodec, ...ctx }),
                handleCriticalError: (_, e: any) => handleError(e.data),
                refreshLockedTitles,
                autofill: {
                    metadata: {
                        fetch: ctx => metadataFetchAutofill(ctx),
                        merge: metadataMergeAutofill,
                    },
                },
            }),
        {
            devTools: true,
        },
    )

    if (state.matches('loading')) {
        return (
            <>
                <Header />
                <Loader center size="normal" />
            </>
        )
    }

    if (state.matches('404')) {
        return (
            <>
                <Header />
                <NotFoundError />
            </>
        )
    }

    if (state.matches('403')) {
        return (
            <>
                <Header />
                <ForbiddenError />
            </>
        )
    }

    return (
        <ResourceMachineServiceContext.Provider value={service}>
            <CharactersProvider resourceId={episodeId}>
                <HeaderEditing />
                <EpisodeNode />
            </CharactersProvider>
        </ResourceMachineServiceContext.Provider>
    )
}

export default EpisodePage
